import { useEffect, useState } from "react";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import { FiCheck, FiX } from "react-icons/fi";
import { MultiSelect } from "react-multi-select-component";
import Switch from "react-switch";

interface FilterModalProps {
  show: boolean;
  onHide: () => void;
  allCategories: { label: string; value: string }[];
  filterValues: {
    productNames: string;
    category: string[];
    status: string;
    minPrice: string;
    maxPrice: string;
    lowStockOnly: boolean;
  };
  setFilterValues: (vals: any) => void;
  onSearch: (filters: {
    productNames: string;
    category: string[];
    status: string;
    minPrice: number;
    maxPrice: number;
    lowStockOnly: boolean;
  }) => void;
  onReset: () => void;
}

const statusOptions = [
  { label: "All", value: "All" },
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" },
];

const PRICE_MIN = 0;
const PRICE_MAX = 1000;
const PRICE_STEP = 1;

const clampPriceValue = (value: number) =>
  Math.min(PRICE_MAX, Math.max(PRICE_MIN, value));

const parsePriceValue = (value: string, fallback: number) => {
  const parsedValue = Number.parseFloat(value);

  if (Number.isNaN(parsedValue)) {
    return fallback;
  }

  return clampPriceValue(parsedValue);
};

const FilterModal = ({
  show,
  onHide,
  allCategories,
  filterValues,
  setFilterValues,
  onSearch,
  onReset,
}: FilterModalProps) => {
  const [allCatsSelected, setAllCatsSelected] = useState<any[]>([]);

  const parsedMin = parsePriceValue(filterValues.minPrice, PRICE_MIN);
  const parsedMax = parsePriceValue(filterValues.maxPrice, PRICE_MAX);
  const sliderMinValue = Math.min(parsedMin, parsedMax);
  const sliderMaxValue = Math.max(parsedMin, parsedMax);
  const sliderRange = PRICE_MAX - PRICE_MIN;
  const minThumbOffset = ((sliderMinValue - PRICE_MIN) / sliderRange) * 100;
  const maxThumbOffset = ((sliderMaxValue - PRICE_MIN) / sliderRange) * 100;

  const handleMinPriceChange = (nextMin: number) => {
    const clampedMin = clampPriceValue(nextMin);
    const nextMax = Math.max(clampedMin, parsedMax);

    setFilterValues({
      ...filterValues,
      minPrice: String(clampedMin),
      maxPrice: String(nextMax),
    });
  };

  const handleMaxPriceChange = (nextMax: number) => {
    const clampedMax = clampPriceValue(nextMax);
    const nextMin = Math.min(parsedMin, clampedMax);

    setFilterValues({
      ...filterValues,
      minPrice: String(nextMin),
      maxPrice: String(clampedMax),
    });
  };

  const handleSearch = () => {
    const selectedCategories = allCatsSelected.map((cat: any) => cat.value);
    const minPrice = Math.min(sliderMinValue, sliderMaxValue);
    const maxPrice = Math.max(sliderMinValue, sliderMaxValue);

    onSearch({
      productNames: filterValues.productNames,
      category: selectedCategories,
      status: filterValues.status,
      minPrice,
      maxPrice,
      lowStockOnly: filterValues.lowStockOnly,
    });

    onHide();
  };

  useEffect(() => {
    const formattedCategories = filterValues.category.map((category) => ({
      label: category,
      value: category,
    }));

    setAllCatsSelected(formattedCategories);
  }, [filterValues.category]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="product-filter-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Product filter</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Control
            className="filter-search-input"
            placeholder="Product 1, Product 2, Product 3, ..."
            value={filterValues.productNames}
            onChange={(e) =>
              setFilterValues({ ...filterValues, productNames: e.target.value })
            }
          />
        </Form.Group>

        <div className="d-flex gap-3 mb-3 category-status-wrapper">
          <div className="flex-grow-1 category">
            <label>Category</label>
            <MultiSelect
              options={allCategories}
              value={allCatsSelected}
              onChange={setAllCatsSelected}
              labelledBy="Select"
              hasSelectAll={false}
              className="cat-select-wrap status"
              valueRenderer={(selected) => {
                if (selected.length === 0) return "All";

                const visible = selected
                  .slice(0, 2)
                  .map((category) => category.label)
                  .join(", ");
                const extra =
                  selected.length > 2 ? `, +${selected.length - 2} more` : "";

                return visible + extra;
              }}
            />
          </div>
          <div className="flex-grow-1 status-select-wrap">
            <Form.Group controlId="statusSelect">
              <Form.Label className="mb-1">Status</Form.Label>
              <Form.Select
                className="form-select-lg-min"
                aria-label="Filter by status"
                value={filterValues.status}
                onChange={(e) =>
                  setFilterValues({ ...filterValues, status: e.target.value })
                }
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
        </div>

        <div className="price-range mb-3">
          <Form.Label className="mb-1">Price</Form.Label>
          <div className="d-flex justify-content-between mb-2">
            <InputGroup style={{ width: 120 }}>
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                size="sm"
                type="text"
                value={filterValues.minPrice}
                onChange={(e) =>
                  setFilterValues({ ...filterValues, minPrice: e.target.value })
                }
                placeholder="Min"
              />
            </InputGroup>
            <InputGroup style={{ width: 120 }}>
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                size="sm"
                type="text"
                value={filterValues.maxPrice}
                onChange={(e) =>
                  setFilterValues({ ...filterValues, maxPrice: e.target.value })
                }
                placeholder="Max"
              />
            </InputGroup>
          </div>

          <div className="price-range-slider mb-3">
            <div
              className="price-range-track"
              aria-hidden="true"
              style={{
                left: `${minThumbOffset}%`,
                right: `${100 - maxThumbOffset}%`,
              }}
            />
            <input
              type="range"
              className="price-range-input price-range-min"
              min={PRICE_MIN}
              max={PRICE_MAX}
              step={PRICE_STEP}
              value={sliderMinValue}
              aria-label="Minimum price"
              onChange={(event) =>
                handleMinPriceChange(Number.parseFloat(event.target.value))
              }
            />
            <input
              type="range"
              className="price-range-input price-range-max"
              min={PRICE_MIN}
              max={PRICE_MAX}
              step={PRICE_STEP}
              value={sliderMaxValue}
              aria-label="Maximum price"
              onChange={(event) =>
                handleMaxPriceChange(Number.parseFloat(event.target.value))
              }
            />
          </div>
        </div>

        <div className="low-stock-toggle mb-3">
          <span className="low-stock-label">Low stock items</span>
          <Switch
            checkedIcon={true}
            checkedHandleIcon={<FiCheck className="switch_tick_icon" />}
            uncheckedHandleIcon={<FiX className="switch_un_tick_icon" />}
            uncheckedIcon={true}
            width={40}
            height={20}
            onHandleColor="#FFFFFF"
            onColor="#00A85B"
            offHandleColor="#9B9B9B"
            offColor="#D8D8D8"
            onChange={() =>
              setFilterValues({
                ...filterValues,
                lowStockOnly: !filterValues.lowStockOnly,
              })
            }
            checked={filterValues.lowStockOnly}
          />
        </div>
      </Modal.Body>

      <Modal.Footer className="justify-content-center">
        <Button className="search-btn" onClick={handleSearch}>
          SEARCH
        </Button>
        <Button variant="outline-success" onClick={onReset}>
          RESET
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FilterModal;
